// Het vlak "Validatie" (VT-1): wat de accountmanager zei, per regel.
//
// Het document zelf vul je in op /validatie/<propositie> (ook als beheerder:
// de owner-ronde gaat vóór de AM-ronde). Dit vlak is het overzicht erna: per
// regel de telling, wie wat zei met de eigen woorden, of de regel vaststaat,
// en — alleen hier — of hij een bewust twijfelgeval is. De telling en de
// vast-stand komen van de server; dit scherm rekent niets.
//
// "Vast" kan hier ook, voor wie het overzicht leest en een regel wil
// dichtzetten zonder terug naar het document te gaan. Het schrijft in de
// gedeelde database (validatie_vast, 0035) en haalt daarna het overzicht
// opnieuw op, zodat er nooit een eigen stand naast die van de server staat.
//
// Terug naar de config gaat NIET vanuit dit scherm: dat is
// tools/validatie_naar_config.py, als verschillijst voor de owner.

import { useState } from "react";

import { Card } from "../../components/ui";
import { LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { ValidatieOverzichtDocument, ValidatieOverzichtRegel } from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";

const BLOK_WOORD: Record<string, string> = {
  vak: "Jullie vak",
  bronnen: "Wat het werkveld leest",
  koppen: "Telt dit voor jullie?",
};

const OORDEEL_KLEUR: Record<string, string> = {
  klopt: color.accentGreen,
  ja: color.accentGreen,
  weg: color.accentRed,
  nee: color.accentRed,
  anders: color.accentBlue,
  twijfel: color.accentAmber,
};

function tijd(op: string | null): string {
  if (!op) return "";
  return new Date(op).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** De korte naam voor de keuzeknop: de titel zonder de vraag erachter. */
function korteNaam(titel: string): string {
  return titel.split(" — ")[0] ?? titel;
}

export function Validatie() {
  const res = useResource(() => api.validatieOverzicht(), []);
  const [keuze, setKeuze] = useState<string | null>(null);
  if (res.error) return <LoadError reason={res.error} onRetry={res.reload} />;
  if (!res.data) return <p style={{ color: color.faint, fontSize: size.meta }}>Laden…</p>;
  if (!res.data.documenten.length) {
    return (
      <p style={{ color: color.soft, fontSize: size.body }}>
        Er is nog geen validatiedocument (config/validatie/ is leeg).
      </p>
    );
  }
  // Release 06-10: één document per pilotpropositie. Eén tegelijk, met een
  // rij knoppen erboven; de keuze is alleen een weergave, de server telt.
  const docs = res.data.documenten;
  const doc = docs.find((d) => d.propositie === keuze) ?? docs[0];
  if (!doc) return null;
  return (
    // minmax(0, 1fr) (reparatie 06-10): een rasterkolom is standaard minstens
    // zo breed als zijn breedste woord-zonder-afbreken; de langste
    // propositieknop duwde de pagina op 390 px 130 px zijwaarts.
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16 }}>
      {docs.length > 1 && (
        <div role="tablist" aria-label="Validatiedocumenten" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {docs.map((d) => {
            const actief = d.propositie === doc.propositie;
            return (
              <button
                key={d.propositie}
                type="button"
                role="tab"
                aria-selected={actief}
                onClick={() => setKeuze(d.propositie)}
                style={{
                  border: `1px solid ${actief ? color.ink : color.border}`,
                  background: actief ? color.ink : "transparent",
                  color: actief ? color.surface : color.body,
                  borderRadius: radius.pill,
                  padding: "6px 12px",
                  fontSize: size.meta,
                  cursor: "pointer",
                  maxWidth: "100%",
                  textAlign: "left",
                }}
              >
                {korteNaam(d.titel)}
                <span style={{ opacity: 0.7 }}>
                  {" "}
                  · {d.personen.length ? `${d.personen.length} pers.` : "nog leeg"}
                </span>
              </button>
            );
          })}
        </div>
      )}
      <Document key={doc.propositie} doc={doc} herlaad={res.reload} />
    </div>
  );
}

function Document({ doc, herlaad }: { doc: ValidatieOverzichtDocument; herlaad: () => void }) {
  const blokken = [...new Set(doc.regels.map((r) => r.blok))];
  return (
    <Card style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "baseline",
        }}
      >
        <h2 style={{ margin: 0, fontSize: size.panelTitle, color: color.ink }}>{doc.titel}</h2>
        <a href={doc.pagina} style={{ fontSize: size.meta, color: color.ink }}>
          Open het document →
        </a>
      </div>
      <p style={{ margin: "6px 0 14px", fontSize: size.meta, color: color.soft }}>
        {doc.totaal} regels om te beoordelen · {doc.vast} vast ·{" "}
        {doc.personen.length
          ? doc.personen
              .map((p) => `${p.naam}${p.isBeheerder ? " (beheer)" : ""}: ${p.tekst}`)
              .join(" · ")
          : "nog niemand heeft geantwoord"}{" "}
        · versie {doc.versie}
      </p>
      <p style={{ margin: "-8px 0 14px", fontSize: size.meta, color: color.soft }}>
        {doc.uitBril
          ? "Afgeleid uit de bril (vakgebieden.yaml en sectors.yaml), zonder echte koppen. "
          : "Handgeschreven document (config/validatie/). "}
        Voor de accountmanager van: {doc.scholen.join(", ")}.
      </p>
      {blokken.map((blok) => (
        <section key={blok} style={{ marginTop: 16 }}>
          <div style={sectionLabel}>{BLOK_WOORD[blok] ?? blok}</div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 6 }}>
            {doc.regels
              .filter((r) => r.blok === blok)
              .map((r) => (
                <RegelRij key={r.id} regel={r} propositie={doc.propositie} herlaad={herlaad} />
              ))}
          </div>
        </section>
      ))}
    </Card>
  );
}

function RegelRij({
  regel,
  propositie,
  herlaad,
}: {
  regel: ValidatieOverzichtRegel;
  propositie: string;
  herlaad: () => void;
}) {
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const zetVast = async (vast: boolean) => {
    setBezig(true);
    setFout(null);
    try {
      await api.validatieVast(propositie, { item: regel.id, vast });
      herlaad();
    } catch (e) {
      setFout(e instanceof Error ? e.message : "Niet gelukt.");
    } finally {
      setBezig(false);
    }
  };
  const telling = Object.entries(regel.telling).filter(([, n]) => n > 0);
  return (
    <div
      data-regel={regel.id}
      style={{
        border: `1px ${regel.vast ? "dashed" : "solid"} ${color.border}`,
        borderRadius: radius.block,
        padding: "10px 12px",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: 6,
      }}
    >
      {/* Op een smal scherm schuift het oordeel onder de tekst (reparatie
          06-10: de vaste rechterkolom duwde de pagina op 390 px zijwaarts). */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <div style={{ fontSize: size.body, color: color.ink }}>{regel.tekst}</div>
          <div style={{ fontSize: size.label, color: color.faint, marginTop: 2 }}>
            {regel.id}
            {regel.doel ? ` → ${regel.doel}` : ""}
          </div>
          {regel.twijfelgeval && (
            <div
              style={{
                marginTop: 6,
                fontSize: size.meta,
                color: color.body,
                background: color.accentAmberBg,
                borderRadius: radius.input,
                padding: "6px 8px",
              }}
            >
              <b style={{ color: color.accentAmber }}>Bewust twijfelgeval.</b>{" "}
              {regel.waaromTwijfel}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontSize: size.meta, color: color.soft }}>
            {regel.vrij
              ? `${regel.antwoorden.length} ingevuld`
              : telling.length
                ? telling.map(([k, n]) => `${n}× ${k}`).join(" · ")
                : "nog geen oordeel"}
          </span>
          <label
            style={{
              display: "inline-flex",
              gap: 5,
              alignItems: "center",
              fontSize: size.meta,
              color: color.soft,
              cursor: bezig ? "default" : "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={regel.vast}
              disabled={bezig}
              onChange={(e) => void zetVast(e.target.checked)}
            />
            vast
          </label>
        </div>
      </div>
      {regel.antwoorden.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 16, display: "grid", gap: 2 }}>
          {regel.antwoorden.map((a, i) => (
            <li key={i} style={{ fontSize: size.meta, color: color.body }}>
              <span style={{ fontWeight: weight.medium }}>{a.persoon}</span>
              {a.isBeheerder ? " (beheer)" : ""}:{" "}
              <span style={{ color: OORDEEL_KLEUR[a.oordeel] ?? color.body, fontWeight: weight.medium }}>
                {a.oordeel}
              </span>
              {a.tekst ? ` — “${a.tekst}”` : ""}{" "}
              <span style={{ color: color.faint }}>{tijd(a.op)}</span>
            </li>
          ))}
        </ul>
      )}
      {fout && (
        <span role="alert" style={{ fontSize: size.meta, color: color.accentRed }}>
          {fout}
        </span>
      )}
    </div>
  );
}
